import React, { useState, useEffect, useRef, useCallback } from 'react';
import LocationAutocomplete from './LocationAutocomplete';

interface AddressData {
  line1: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  formatted: string;
}

interface AddressFormWithAutocompleteProps {
  onAddressChange: (address: AddressData, phone: string, isComplete: boolean) => void;
  onAddressSelect?: () => void;
  initialAddress?: AddressData;
  initialPhone?: string;
}

const AddressFormWithAutocomplete: React.FC<AddressFormWithAutocompleteProps> = ({
  onAddressChange,
  onAddressSelect,
  initialAddress,
  initialPhone = '',
}) => {
  const [address, setAddress] = useState({
    line1: initialAddress?.line1 || '',
    city: initialAddress?.city || '',
    state: initialAddress?.state || '',
    postal_code: initialAddress?.postal_code || '',
    country: initialAddress?.country || 'US',
  });
  const [phone, setPhone] = useState(initialPhone);
  const formRef = useRef<HTMLDivElement>(null);

  // Sync address state when initialAddress changes
  useEffect(() => {
    if (initialAddress) {
      setAddress({
        line1: initialAddress.line1 || '',
        city: initialAddress.city || '',
        state: initialAddress.state || '',
        postal_code: initialAddress.postal_code || '',
        country: initialAddress.country || 'US',
      });
    }
  }, [initialAddress]);

  // Notify parent whenever address or phone changes
  const notifyParent = useCallback((addr: typeof address, ph: string) => {
    const parts = [addr.line1, addr.city, addr.state, addr.postal_code].filter(Boolean);
    const formatted = parts.join(', ');
    const isComplete = !!(addr.line1 && addr.city && addr.state && addr.postal_code);
    onAddressChange({ ...addr, formatted, country: addr.country || 'US' }, ph, isComplete);
  }, [onAddressChange]);

  const updateField = useCallback((field: string, value: string) => {
    setAddress(prev => {
      const next = { ...prev, [field]: value };
      setTimeout(() => notifyParent(next, phone), 0);
      return next;
    });
  }, [notifyParent, phone]);

  // Detect browser autofill by polling DOM values
  // Mobile Safari/Chrome don't always fire change events on autofill
  useEffect(() => {
    const form = formRef.current;
    if (!form) return;

    const syncFromDOM = () => {
      const addrInput = form.querySelector('[name="street-address"]') as HTMLInputElement;
      const cityInput = form.querySelector('[name="address-level2"]') as HTMLInputElement;
      const stateInput = form.querySelector('[name="address-level1"]') as HTMLInputElement;
      const zipInput = form.querySelector('[name="postal-code"]') as HTMLInputElement;
      const phoneInput = form.querySelector('[name="tel"]') as HTMLInputElement;

      const domLine1 = addrInput?.value?.trim() || '';
      const domCity = cityInput?.value?.trim() || '';
      const domState = stateInput?.value?.trim() || '';
      const domZip = zipInput?.value?.replace(/[^\d-]/g, '') || '';
      const domPhone = phoneInput?.value || '';

      setAddress(prev => {
        const changed =
          (domLine1 && domLine1 !== prev.line1) ||
          (domCity && domCity !== prev.city) ||
          (domState && domState !== prev.state) ||
          (domZip && domZip !== prev.postal_code);
        if (!changed) return prev;

        const next = {
          ...prev,
          line1: domLine1 || prev.line1,
          city: domCity || prev.city,
          state: domState || prev.state,
          postal_code: domZip || prev.postal_code,
        };
        const phoneVal = domPhone || phone;
        setTimeout(() => notifyParent(next, phoneVal), 0);
        return next;
      });
      if (domPhone && domPhone !== phone) {
        setPhone(domPhone);
      }
    };

    // Poll for autofill values for the first 3 seconds
    const pollInterval = setInterval(syncFromDOM, 500);
    const pollTimeout = setTimeout(() => clearInterval(pollInterval), 3000);

    // Also listen for Chrome animation-based autofill detection
    const handleAutofill = () => setTimeout(syncFromDOM, 50);
    form.addEventListener('animationstart', handleAutofill);

    return () => {
      clearInterval(pollInterval);
      clearTimeout(pollTimeout);
      form.removeEventListener('animationstart', handleAutofill);
    };
  }, [notifyParent, phone]);

  const handlePlaceSelect = useCallback((place: {
    address: string;
    city: string;
    state: string;
    stateAbbreviation: string;
    postalCode?: string;
  }) => {
    const addressParts = place.address.split(',').map((s) => s.trim());
    const line1 = addressParts[0] || place.address;

    let postalCode = place.postalCode || '';
    if (!postalCode && place.address) {
      const zipMatch = place.address.match(/\b(\d{5}(-\d{4})?)\b/);
      if (zipMatch) postalCode = zipMatch[1];
    }
    // Fallback: parse city, state from comma-separated parts (e.g. "123 Main St, Sacramento, CA 95814, USA")
    let city = place.city || '';
    let state = place.stateAbbreviation || place.state || '';
    if (!city && addressParts.length >= 2) city = addressParts[1];
    if (!state && addressParts.length >= 3) {
      const stateZip = addressParts[2];
      const stateMatch = stateZip?.match(/^([A-Za-z]{2})\s+\d{5}/);
      if (stateMatch) state = stateMatch[1];
      else state = stateZip?.replace(/\s*\d{5}(-\d{4})?.*$/, '').trim() || stateZip?.split(/\s+/)[0] || '';
    }
    if (!postalCode && addressParts.length >= 3) {
      const zipMatch = addressParts[2]?.match(/(\d{5}(-\d{4})?)/);
      if (zipMatch) postalCode = zipMatch[1];
    }

    const newAddress = {
      line1,
      city: city || '',
      state: state || '',
      postal_code: postalCode || '',
      country: 'US',
    };

    setAddress(newAddress);
    notifyParent(newAddress, phone);
    onAddressSelect?.();
  }, [notifyParent, phone, onAddressSelect]);

  return (
    <div ref={formRef} className="space-y-4">
      {/* Hidden country for browser autofill context */}
      <input type="hidden" name="country" autoComplete="country" value="US" />
      
      <div>
        <label htmlFor="address-line1" className="block text-sm font-medium text-gray-700 mb-1.5">
          Enter address <span className="text-red-500">*</span>
        </label>
        <LocationAutocomplete
          inputId="address-line1"
          onPlaceSelect={handlePlaceSelect}
          onInputChange={(val) => updateField('line1', val)}
          value={address.line1}
          placeholder="123 Main St"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-base"
        />
        <p className="text-xs text-gray-500 mt-1">Start typing for suggestions, or use browser autofill</p>
      </div>
      
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1.5">
            City <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="city"
            name="address-level2"
            autoComplete="address-level2"
            enterKeyHint="next"
            value={address.city}
            onChange={(e) => updateField('city', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-base"
            required
          />
        </div>
        
        <div>
          <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-1.5">
            State <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="state"
            name="address-level1"
            autoComplete="address-level1"
            enterKeyHint="next"
            value={address.state}
            onChange={(e) => updateField('state', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-base"
            placeholder="CA"
            maxLength={2}
            required
          />
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor="postal_code" className="block text-sm font-medium text-gray-700 mb-1.5">
            ZIP Code <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="postal_code"
            name="postal-code"
            autoComplete="postal-code"
            inputMode="numeric"
            enterKeyHint="next"
            value={address.postal_code || ''}
            onChange={(e) => {
              const zipValue = e.target.value.replace(/[^\d-]/g, '');
              updateField('postal_code', zipValue);
            }}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-base"
            placeholder="95814"
            maxLength={10}
            required
          />
          {address.postal_code && (
            <p className="text-xs text-green-600 mt-1">✓ {address.postal_code}</p>
          )}
        </div>
        
        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1.5">
            Phone <span className="text-red-500">*</span>
          </label>
          <input
            type="tel"
            id="phone"
            name="tel"
            autoComplete="tel"
            inputMode="tel"
            enterKeyHint="done"
            value={phone}
            onChange={(e) => {
              const newPhone = e.target.value;
              setPhone(newPhone);
              setTimeout(() => notifyParent(address, newPhone), 0);
            }}
            placeholder="(555) 123-4567"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-base"
            required
          />
        </div>
      </div>
    </div>
  );
};

export default AddressFormWithAutocomplete;
